import { useMutation } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import moment from 'moment';
import React, { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Button, Dialog, Portal, Text, TextInput } from 'react-native-paper';
import useConfirmModal from 'shared/hooks/useConfirmModal';
import useOrientation from 'shared/hooks/useOrientation';
import useSnackBar from 'shared/hooks/useSnackBar';
import { sizes } from 'shared/styles';
import DatePicker from 'shared/components/DatePicker';

interface Props {
  trackId: string;
  onDismiss: () => void;
  onSuccess: (trackId: string) => void;
}

const DUPLICATE_COMP_MUTATION = gql`
  mutation DuplicateComp($input: DuplicateCompInput!) {
    trackDuplicateComp(input: $input) {
      id
      name
      compDate
    }
  }
`;

const DuplicateCompModal = ({ trackId, onDismiss, onSuccess }: Props) => {
  const confirmWith = useConfirmModal();
  const showSnackBar = useSnackBar();
  const { vh } = useOrientation();
  const [name, setName] = useState('');
  const [compDate, setCompDate] = useState<string>(
    moment().format('YYYY-MM-DD')
  );

  const [duplicateComp, { loading }] = useMutation(DUPLICATE_COMP_MUTATION);

  const displayError = (message: string) => {
    confirmWith({ title: 'Error', message, hideCancel: true });
  };

  const onSave = () => {
    if (!name.trim()) {
      displayError('Please enter a name for the competition');
      return;
    }

    duplicateComp({
      variables: {
        input: {
          trackId,
          name: name.trim(),
          compDate,
        },
      },
    })
      .then(({ data }) => {
        onSuccess(data.trackDuplicateComp.id);
        showSnackBar({
          message: `Successfully duplicated competition: ${data.trackDuplicateComp.name}`,
        });
      })
      .catch(({ graphQLErrors }) => {
        const message =
          graphQLErrors && graphQLErrors.map((e: any) => e.message).join(', ');
        displayError(
          message || 'Error duplicating competition. Please, try again!'
        );
      });
  };

  return (
    <Portal>
      <Dialog
        visible
        onDismiss={onDismiss}
        style={[{ maxHeight: vh(60) }, styles.dialog]}
      >
        <Dialog.Title>Duplicate Competition</Dialog.Title>
        <View
          style={{
            marginHorizontal: 20,
            marginBottom: sizes.spacing,
            justifyContent: 'center',
          }}
        >
          <TextInput
            label="Name"
            mode="outlined"
            value={name}
            onChangeText={setName}
            style={{ marginBottom: 20 }}
            placeholder="Enter competition name"
          />
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              marginBottom: 20,
            }}
          >
            <Text style={{ marginRight: 20 }}>Comp Date:</Text>
            <DatePicker
              date={moment(compDate)}
              onConfirm={(date) => {
                setCompDate(date.format('YYYY-MM-DD'));
              }}
              title="Date"
            />
          </View>
        </View>

        <Dialog.Actions style={styles.actionsContainer}>
          <Button mode="outlined" onPress={onDismiss}>
            Cancel
          </Button>
          <Button
            loading={loading}
            disabled={loading || !name.trim()}
            mode="contained"
            icon="content-copy"
            onPress={onSave}
          >
            Duplicate
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
};

const styles = StyleSheet.create({
  dialog: Platform.select({
    default: {},
    web: {
      alignSelf: 'center',
      width: 500,
    },
  }),
  actionsContainer: {
    paddingVertical: sizes.spacing,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
  },
});

export default DuplicateCompModal;
